import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import PageHeader from '../components/PageHeader';
import { useData } from '../context/DataContext';
import { Target, Eye, ShieldCheck, Award, Users, Factory, Zap, Leaf, Sprout, TrendingUp, Quote, X, ArrowRight } from 'lucide-react';
import Translate from '../components/Translate';
import { useTranslation } from 'react-i18next';
import { getLangText } from '../utils/langHelper';

const About = () => {
  const { data } = useData();
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const [isChairmanModalOpen, setIsChairmanModalOpen] = useState(false);
  const chairmanObj = data.about?.chairman || {};
  const directors = data.about?.directors || [];

  const chairman = {
    ...chairmanObj,
    name: getLangText(chairmanObj, 'name', lang),
    position: getLangText(chairmanObj, 'position', lang),
    message: getLangText(chairmanObj, 'message', lang)
  };
  const milestones = [
    { year: '1984', title: 'Foundation', desc: 'Started with a 1,250 TCD crushing capacity mill.', icon: Sprout },
    { year: '1995', title: 'Expansion', desc: 'Increased capacity to 5,000 TCD and added distillery.', icon: TrendingUp },
    { year: '2005', title: 'Power Generation', desc: 'Commissioned 24MW bagasse-based co-generation plant.', icon: Zap },
    { year: '2015', title: 'Digitalization', desc: 'Launched farmer mobile app and automated procurement.', icon: Users },
    { year: '2023', title: 'Sustainability Milestone', desc: 'Achieved net-zero carbon footprint in manufacturing.', icon: Leaf },
  ];

  const values = [
    { title: 'Quality First', desc: 'Delivering superior quality and food safety in every jaggery and jaggery powder product.', icon: ShieldCheck },
    { title: 'Farmer Prosperity', desc: 'Supporting farmers through modern farming guidance, planning, and reliable services.', icon: Users },
    { title: 'Innovation', desc: 'Continuously adopting modern technology to improve quality, productivity, and efficiency.', icon: Factory },
    { title: 'Environmental Responsibility', desc: 'Promoting eco-friendly manufacturing through organic fertilizer production and sustainable resource utilization.', icon: Award },
  ];

  return (
    <div className="relative">

      <PageHeader 
        title={<Translate>About Sangram Cane Agro</Translate>} 
        subtitle={<Translate>Legacy • Quality • Trust</Translate>} 
      />

      {/* ── 1. OUR LEGACY & FACTORY HISTORY ── */}
      <section className="py-16 lg:py-20 bg-section-gradient relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
            
            {/* Image Card */}
            <motion.div 
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="lg:col-span-5 relative"
            >
              <div className="p-2 bg-white rounded-2xl border border-gray-100 shadow-premium-lg relative overflow-hidden group">
                <div className="overflow-hidden rounded-xl aspect-[4/3] relative">
                  <img 
                    src="/sangram factory.png"
                    alt="Sugarcane Processing Factory"
                    className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-forest/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                </div>
              </div>
              
              <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-gold/10 rounded-full blur-2xl -z-10"></div>
              <div className="absolute -top-4 -left-4 w-20 h-20 bg-emerald/10 rounded-full blur-2xl -z-10"></div>
            </motion.div>

            {/* Content */}
            <motion.div 
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="lg:col-span-7 space-y-6"
            >
              <span className="section-label block"><Translate>Our Legacy & History</Translate></span>
              <h2 className="section-title">
                <Translate>Trust, Quality &</Translate>{' '}
                <span className="text-emerald"><Translate>Prosperity Journey</Translate></span>
              </h2>
              <div className="gold-divider"></div>
              
              <p className="text-slate text-base lg:text-lg leading-relaxed font-medium">
                <Translate>Sangram Cane Agro is a farmer-owned and farmer-driven company committed to supporting the agricultural community while delivering premium-quality natural products. Using carefully selected sugarcane grown in the fertile fields of Sangli district, we produce Natural Jaggery, Jaggery Powder, Bagasse Briquettes, and Organic Fertilizer by combining traditional practices with modern processing standards. Every stage of production is carried out with strict hygiene, quality control, and food safety measures to ensure that our customers receive 100% natural, chemical-free, and nutritious products.</Translate>
              </p>
              
              <p className="text-slate text-base leading-relaxed">
                <Translate>Our mission is to provide farmers with fair value for their hard work while offering consumers pure, healthy, and authentic sweetness they can trust. We are guided by four core values—quality, transparency, sustainability, and customer satisfaction.</Translate>
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── 2. LEADERSHIP STATEMENT (FOUNDER'S MESSAGE) ── */}
      <section className="py-20 lg:py-24 bg-[#FAF9F5] border-t border-b border-gray-200/40 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            
            {/* Image (Left Column) */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="lg:col-span-5"
            >
              <div className="relative">
                <div className="absolute -inset-3 bg-gradient-to-br from-sage/20 to-emerald/10 rounded-2xl -z-10 -rotate-1"></div>
                <img 
                  src={chairman.image || 'https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&q=80&w=600'} 
                  alt="Founder" 
                  className="rounded-xl shadow-premium-xl w-full object-cover aspect-[3/4] border-2 border-white"
                />
              </div>
            </motion.div>

            {/* Text and quote statement (Right Column) */}
            <motion.div 
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-7 space-y-6"
            >
              <div className="flex items-center space-x-2.5">
                <div className="p-2 bg-emerald/10 rounded-lg text-emerald">
                  <Quote size={16} />
                </div>
                <span className="section-label"><Translate>Leadership Statement</Translate></span>
              </div>
              <h2 className="section-title">
                <Translate>From the Founder's Desk</Translate>
              </h2>
              <div className="w-12 h-[2px] bg-gradient-to-r from-sage to-emerald rounded-full"></div>
              
              <div className="text-slate text-base lg:text-lg leading-[1.8] italic pl-6 border-l-2 border-emerald/30 font-body">
                <p>
                  "
                  <Translate>
                    A visionary leader whose unwavering dedication, determination, and commitment to excellence have earned him the admiration and trust of the community, Bapusaheb ("Bapu") Yesugade is widely regarded as one of the most respected personalities in the region. His relentless pursuit of progress, coupled with his passion for serving society, has been the driving force behind his remarkable journey. Bapusaheb firmly believes that sustainable growth is achieved through hard work, clear vision, and unwavering determination. Guided by this philosophy, he established Sangram Udyog with the objective of contributing to the holistic development of society.
                  </Translate>
                  "
                </p>
              </div>
              
              <div className="pt-2 pl-6">
                <h4 className="text-xl font-heading font-extrabold text-charcoal"><Translate>{chairman.name || "Late. Vithhalrao ( Bapusaheb ) Yesugade"}</Translate></h4>
                <p className="text-emerald font-heading font-bold text-xs uppercase tracking-wider mt-0.5"><Translate>{chairman.position || "FOUNDER & CHAIRMAN"}</Translate></p>
              </div>

              <div className="pt-4 pl-6">
                <button 
                  onClick={() => setIsChairmanModalOpen(true)}
                  className="px-6 py-3 bg-transparent border-2 border-emerald text-emerald rounded-xl font-heading font-semibold hover:bg-emerald hover:text-white transition-all duration-300 inline-flex items-center group text-xs cursor-pointer"
                >
                  <Translate>View Founder Address</Translate>
                  <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={14} />
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Chairman / Founder Full Message Modal */}
      <AnimatePresence>
        {isChairmanModalOpen && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsChairmanModalOpen(false)}
            className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-6 bg-charcoal/75 backdrop-blur-md overflow-y-auto"
          >
            <motion.div 
              initial={{ scale: 0.95, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-3xl border border-gray-150 shadow-premium-xl max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden relative"
            >
              {/* Sticky Top Header with Close Button */}
              <div className="sticky top-0 z-50 bg-white/95 backdrop-blur-md px-6 py-4 border-b border-gray-100 flex justify-between items-center flex-shrink-0">
                <div className="flex items-center space-x-2 text-emerald">
                  <Quote size={18} />
                  <span className="text-xs font-heading font-extrabold uppercase tracking-widest text-charcoal">
                    <Translate>Founder's Address</Translate>
                  </span>
                </div>
                <button 
                  onClick={() => setIsChairmanModalOpen(false)}
                  className="p-2 bg-gray-100 hover:bg-emerald hover:text-white rounded-full text-charcoal transition-colors cursor-pointer shadow-sm flex items-center justify-center"
                  aria-label="Close modal"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Scrollable Body Content */}
              <div className="overflow-y-auto flex-1 p-5 sm:p-8">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-start">
                  {/* Left Portrait Column */}
                  <div className="md:col-span-5 bg-ivory/50 p-6 rounded-2xl flex flex-col items-center justify-center border border-gray-100">
                    <div className="relative w-40 h-52 sm:w-48 sm:h-64 md:w-full md:h-80 rounded-2xl overflow-hidden shadow-premium border-2 border-sage/20">
                      <img 
                        src={chairman.image || 'https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&q=80&w=600'} 
                        alt={chairman.name || "Founder"} 
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="text-center mt-4">
                      <h3 className="text-lg sm:text-xl font-heading font-extrabold text-charcoal">
                        <Translate>{chairman.name || "Late. Vithhalrao ( Bapusaheb ) Yesugade"}</Translate>
                      </h3>
                      <p className="text-emerald font-heading font-bold text-xs uppercase tracking-wider mt-1">
                        <Translate>{chairman.position || "FOUNDER & CHAIRMAN"}</Translate>
                      </p>
                    </div>
                  </div>

                  {/* Right Message Column */}
                  <div className="md:col-span-7 space-y-4 sm:p-2">
                    <div className="text-slate text-sm sm:text-base leading-relaxed space-y-4 font-body">
                      {(chairman.message || "A visionary leader whose unwavering dedication, determination, and commitment to excellence have earned him the admiration and trust of the community, Late. Vithhalrao ( Bapusaheb ) Yesugade is widely regarded as one of the most respected pioneers in the region's agro-industrial sector. Under his guidance, Sangram Cane Agro has achieved monumental growth, fostering farmer empowerment and industrial excellence across Maharashtra.").split('\n').map((paragraph, idx) => (
                        <p key={idx}>
                          <Translate>{paragraph}</Translate>
                        </p>
                      ))}
                    </div>

                    <div className="border-t border-gray-100 pt-6 mt-6 flex justify-end">
                      <button
                        onClick={() => setIsChairmanModalOpen(false)}
                        className="px-6 py-2.5 bg-emerald text-white hover:bg-forest rounded-xl font-heading font-bold text-xs uppercase tracking-wider transition-colors shadow-md cursor-pointer"
                      >
                        <Translate>Close Address</Translate>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* ── 3. OUR TEAM (BOARD OF DIRECTORS) ── */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-12 gap-4">
            <div>
              <h2 className="section-title">
                <Translate>Board of Directors</Translate>
              </h2>
            </div>
            <div className="w-16 h-[2px] bg-gradient-to-r from-sage to-emerald rounded-full"></div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {directors.map((leader, index) => {
              const leaderName = getLangText(leader, 'name', lang);
              const leaderRole = getLangText(leader, 'role', lang);
              const leaderDesc = getLangText(leader, 'desc', lang);

              return (
                <motion.div
                  key={leader.id || index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="group relative"
                >
                  <div className="bg-white rounded-2xl border border-gray-150 p-4 shadow-sm hover:shadow-xl hover:border-emerald/40 hover:-translate-y-2 transition-all duration-300 ease-out flex flex-col justify-between h-full cursor-pointer">
                    <div className="relative overflow-hidden rounded-xl aspect-[4/5] bg-mist mb-3.5">
                      <img 
                        src={leader.image} 
                        alt={leaderName}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      {leaderDesc && (
                        <div className="absolute inset-0 bg-gradient-to-t from-forest/90 via-forest/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-3.5">
                          <p className="text-white/90 text-[11px] font-body leading-relaxed line-clamp-5 italic">
                            "<Translate>{leaderDesc}</Translate>"
                          </p>
                        </div>
                      )}
                    </div>
                    <div className="px-1 text-center lg:text-left">
                      <h3 className="text-sm sm:text-base font-heading font-extrabold text-charcoal group-hover:text-emerald transition-colors line-clamp-1">
                        <Translate>{leaderName}</Translate>
                      </h3>
                      <p className="text-emerald-700 text-xs font-heading font-bold uppercase tracking-wide mt-1">
                        <Translate>{leaderRole}</Translate>
                      </p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── 4. OUR JOURNEY (TIMELINE) ── */}
      <section className="py-16 bg-ivory relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="section-label block mb-2"><Translate>Our Journey</Translate></span>
            <h2 className="section-title mb-4"><Translate>Historic Milestones</Translate></h2>
            <div className="gold-divider mx-auto"></div>
          </motion.div>

          <div className="relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-[2px] bg-gradient-to-b from-transparent via-gold/30 to-transparent hidden md:block"></div>
            
            <div className="space-y-6 lg:space-y-8">
              {milestones.map((item, idx) => (
                <div key={idx} className={`flex flex-col md:flex-row items-center ${idx % 2 === 0 ? 'md:flex-row-reverse' : ''}`}>
                  <div className="flex-1 w-full md:w-1/2 p-2">
                    <motion.div 
                      initial={{ opacity: 0, x: idx % 2 === 0 ? 30 : -30 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5 }}
                      className="group premium-card p-6 relative"
                    >
                      <div className="flex items-start space-x-4">
                        <div className="w-12 h-12 bg-ivory rounded-xl flex-shrink-0 flex items-center justify-center group-hover:bg-emerald/10 transition-colors">
                          <item.icon className="text-emerald" size={24} />
                        </div>
                        <div>
                          <div className="flex items-center space-x-3 mb-1.5">
                            <span className="px-2.5 py-0.5 bg-gold/10 text-gold-dark rounded-md text-[9px] font-heading font-extrabold uppercase tracking-widest">{item.year}</span>
                          </div>
                          <h3 className="text-base font-heading font-bold mb-1.5 text-charcoal">
                            <Translate>{item.title}</Translate>
                          </h3>
                          <p className="text-slate text-xs leading-relaxed">
                            <Translate>{item.desc}</Translate>
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  </div>
                  
                  {/* Timeline node */}
                  <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center z-10 border-4 border-gold/20 shadow-premium my-6 md:my-0 group hover:border-gold transition-colors duration-300">
                    <div className="w-2.5 h-2.5 bg-gold rounded-full"></div>
                  </div>
                  
                  <div className="flex-1 hidden md:block"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── 5. PURPOSE & STRATEGIC FOCUS (MISSION & VISION) ── */}
      <section className="py-20 bg-white relative overflow-hidden border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
            
            {/* Left Column */}
            <div className="lg:col-span-5 space-y-8">
              <div className="space-y-4">
                <span className="section-label block"><Translate>Purpose & Strategic Focus</Translate></span>
                <h2 className="section-title leading-relaxed md:leading-[1.45]">
                  <span className="block mb-3">
                    <Translate>Pure Jaggery, Empowered</Translate>
                  </span>
                  <span className="block">
                    <Translate>Farmers, Sustainable Agriculture</Translate>
                  </span>
                </h2>
              </div>
              <div className="gold-divider"></div>
              <p className="text-slate text-base leading-relaxed">
                <Translate>We produce premium-quality chemical-free jaggery, jaggery powder, and organic fertilizer using modern technology and natural processes. Our goal is to improve sugarcane productivity, support farmers, and promote sustainable agriculture.</Translate>
              </p>
              
              <div className="relative pt-6 hidden lg:block">
                <div className="relative w-36 h-36 flex items-center justify-center">
                  <motion.div 
                    animate={{ rotate: 360 }}
                    transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
                    className="absolute inset-0 rounded-full border-2 border-dashed border-gold/30"
                  />
                  <motion.div 
                    animate={{ rotate: -360 }}
                    transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
                    className="absolute inset-3 rounded-full border border-emerald/10"
                  />
                  <motion.div
                    animate={{ scale: [1, 1.1, 1] }}
                    transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                    className="w-16 h-16 bg-emerald/10 rounded-full flex items-center justify-center text-emerald"
                  >
                    <Leaf size={32} className="transform rotate-45" />
                  </motion.div>
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div className="lg:col-span-7 relative min-h-[500px] flex flex-col justify-center">
              
              {/* Mission Card */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="premium-card p-10 max-w-xl relative z-20 mr-12 lg:mb-[-40px]"
              >
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 bg-emerald/10 rounded-xl flex items-center justify-center text-emerald">
                    <Target size={24} />
                  </div>
                  <div>
                    <span className="text-[10px] text-gold font-heading font-extrabold uppercase tracking-widest block">
                      <Translate>Direction</Translate>
                    </span>
                    <h3 className="text-xl font-heading font-extrabold text-charcoal"><Translate>Our Mission</Translate></h3>
                  </div>
                </div>
                
                <p className="text-slate text-sm leading-relaxed mb-6">
                  <Translate>To manufacture premium-quality jaggery, jaggery powder, and organic fertilizer through advanced technology and farmer-centric practices while delivering safe, natural, and reliable products.</Translate>
                </p>

                <div className="flex flex-wrap gap-2">
                  {['Quality First', 'Farmer Prosperity', 'Natural Products'].map(tag => (
                    <span key={tag} className="px-3 py-1 bg-ivory border border-gray-100 rounded-full text-[10px] font-heading font-bold text-slate">
                      <Translate>{tag}</Translate>
                    </span>
                  ))}
                </div>
              </motion.div>

              {/* Vision Card */}
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="bg-forest text-white p-10 rounded-2xl shadow-premium-xl max-w-xl ml-auto relative z-10 mt-12 lg:mt-6 lg:translate-y-6
                           hover:shadow-emerald hover:-translate-y-1 transition-all duration-300"
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-bl-full pointer-events-none"></div>
                
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 bg-white/10 backdrop-blur-md rounded-xl flex items-center justify-center border border-white/10 text-white">
                    <Eye size={24} />
                  </div>
                  <div>
                    <span className="text-[10px] text-gold-light font-heading font-extrabold uppercase tracking-widest block">
                      <Translate>Horizon</Translate>
                    </span>
                    <h3 className="text-xl font-heading font-extrabold text-white"><Translate>Our Vision</Translate></h3>
                  </div>
                </div>

                <p className="text-white/70 text-sm leading-relaxed mb-8">
                  <Translate>To become India's most trusted producer of natural jaggery, jaggery powder, and organic fertilizer while creating sustainable value for farmers, customers, and the environment.</Translate>
                </p>

                <div className="flex flex-wrap gap-2 border-t border-white/10 pt-6">
                  {['Sustainable Agriculture', 'Natural Quality', 'Farmer Growth'].map(tag => (
                    <span key={tag} className="px-3 py-1 bg-white/10 border border-white/10 rounded-full text-[10px] font-heading font-bold text-white/80">
                      <Translate>{tag}</Translate>
                    </span>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* ── 6. OUR PRINCIPLES (CORE VALUES) ── */}
      <section className="py-20 bg-ivory relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="section-label block mb-2"><Translate>Our Principles</Translate></span>
            <h2 className="section-title mb-4"><Translate>Core Values</Translate></h2>
            <div className="gold-divider mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v, idx) => (
              <motion.div 
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="text-center premium-card p-8 group relative overflow-hidden"
              >
                <div className="w-14 h-14 bg-ivory rounded-xl flex items-center justify-center mx-auto mb-6 group-hover:bg-emerald group-hover:scale-105 transition-all duration-500">
                  <v.icon className="text-emerald group-hover:text-white" size={24} />
                </div>
                <h3 className="text-base font-heading font-bold mb-3 text-charcoal">
                  <Translate>{v.title}</Translate>
                </h3>
                <p className="text-slate text-xs leading-relaxed">
                  <Translate>{v.desc}</Translate>
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
